import { useCallback, useEffect, useRef, useState } from "react";

export const UNDO_WINDOW_MS = 3000;

export interface UndoBatch<T> {
  pending: ReadonlyMap<string, T>;
  schedule: (id: string, item: T) => void;
  undo: () => void;
  /** A no-op when no batch is open. */
  flush: () => void;
}

/**
 * The grace window before an optimistic action commits. A further `schedule` restarts the window, so
 * the batch commits as one. A batch still open at unmount commits rather than being dropped, and
 * React discards that commit's state writes, so only its mutations reach the server. The optimistic
 * overlay and the rollback on failure are [onCommit]'s concern.
 *
 * `schedule`, `undo` and `flush` have stable identities, so one batch's handler can settle a sibling
 * batch without re-creating any of them.
 */
export function useUndoBatch<T>(onCommit: (batch: ReadonlyMap<string, T>) => void): UndoBatch<T> {
  const [pending, setPending] = useState<Map<string, T>>(() => new Map());
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  //
  // Callers re-create `onCommit` every render, so this reads it through a ref instead of making it a
  // dependency of the actions below.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const commit = useCallback(() => {
    const batch = pendingRef.current;
    if (batch.size === 0) return;

    setPending(new Map());
    commitRef.current(batch);
  }, []);

  const schedule = useCallback(
    (id: string, item: T) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      setPending((prev) => new Map(prev).set(id, item));

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commit();
      }, UNDO_WINDOW_MS);
    },
    [commit],
  );

  const undo = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = null;
    setPending(new Map());
  }, []);

  const flush = useCallback(() => {
    if (!timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;
    commit();
  }, [commit]);

  // The timer guard makes StrictMode's mount-time cleanup (pending still empty) a no-op.
  useEffect(
    () => () => {
      if (!timerRef.current) return;

      clearTimeout(timerRef.current);
      timerRef.current = null;
      commitRef.current(pendingRef.current);
    },
    [],
  );

  return { pending, schedule, undo, flush };
}
